import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';
import { useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import { alertsSelectorAdd } from '../../../../recoil';
import { AgentModel, AgentRelationModel, AgentRelationState } from '../../../../../gamma-api';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../utils/urlUtil';
import { defaultApi } from '../../../../apis';
import IbTypography from '../../../../components/common/IbTypography';
import IbButton from '../../../../components/common/IbButton';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import { SourcesEmptyLogo } from '../../../../assets';
import IbIcon from '../../../../components/common/IbIcon';

import {
  EMPTY_AGENT_RELATION,
  EMPTY_CONTENT_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  MAIN_CLASS_NAME,
  RELATIONS_PAGE_SIZE,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
} from './const';
import RelationModal from './RelationModal';
import RelationTable from './RelationTable';

export interface IRelationsTabProps {
  managingAgentId: string;
}

const RelationsTab: React.FC<IRelationsTabProps> = ({ managingAgentId }) => {
  const { push } = useHistory();
  const { t } = useTranslation();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [itemLoading, setItemLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [creationRelation, setCreationRelation] = useState<AgentRelationModel>();
  const [editingRelation, setEditingRelation] = useState<AgentRelationModel>();
  const [deletionRelation, setDeletionRelation] = useState<AgentRelationModel>();

  const [relationList, setRelationList] = useState<AgentRelationModel[]>([]);

  const [relationPagination, setRelationPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: RELATIONS_PAGE_SIZE,
    total: 0,
  });

  const relationListIsEmpty = !listLoading && !relationList.length;

  const fetchRelationList = async () => {
    try {
      const response = await defaultApi.searchAgentRelations(
        [managingAgentId],
        undefined,
        (relationPagination.current || 1) - 1,
        RELATIONS_PAGE_SIZE
      );
      setRelationList(response.data.items || []);
      setRelationPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: t('Agent: Relation: Failed to load relations'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const initialLoadAsync = async () => {
    setListLoading(true);
    await fetchRelationList();
    setListLoading(false);
  };

  const onManagingAgentIdPropChange = () => {
    initialLoadAsync().finally();
  };

  useEffect(onManagingAgentIdPropChange, [managingAgentId]);

  const onRelationPageChange = async () => {
    setPageLoading(true);
    await fetchRelationList();
    setPageLoading(false);
  };

  useEffect(() => {
    onRelationPageChange().finally();
  }, [relationPagination.current, relationPagination.pageSize]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== relationPagination.current) {
      setRelationPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (relationPagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, relationPagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [relationPagination.current]);

  const onCreationButtonClick = () => {
    setCreationRelation({ ...EMPTY_AGENT_RELATION, managing_agent: { agent_id: managingAgentId } as AgentModel });
  };

  const onRelationPaginationChange = (page: number) => setRelationPagination({ ...relationPagination, current: page });

  const onRelationModalClose = () => {
    setEditingRelation(undefined);
    setCreationRelation(undefined);
  };

  const onRelationModalSave = async (relation?: AgentRelationModel) => {
    if (!relation) {
      return;
    }

    try {
      if (relation.relation_id) {
        await defaultApi.updateAgentRelation(relation.relation_id, {
          managing_agent_id: managingAgentId,
          managed_agent_id: relation.managed_agent.agent_id,
          state: relation.state,
          handoff: relation.handoff,
        });
      } else {
        await defaultApi.createAgentRelation({
          managing_agent_id: managingAgentId,
          managed_agent_id: relation.managed_agent.agent_id,
          state: relation.state,
          handoff: relation.handoff,
        });
      }

      await fetchRelationList();
      setEditingRelation(undefined);
      setCreationRelation(undefined);
    } catch (err) {
      addAlert({ content: t('Agent: Relation: Failed to save relation'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onRelationStateChange = async (relation: AgentRelationModel, state: AgentRelationState) => {
    try {
      await defaultApi.updateAgentRelation(relation.relation_id, {
        managing_agent_id: relation.managing_agent.agent_id,
        managed_agent_id: relation.managed_agent.agent_id,
        state,
        handoff: relation.handoff,
      });
      await fetchRelationList();
    } catch (err) {
      addAlert({ content: t('Agent: Relation: Failed to save relation'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onRelationDelete = (relation: AgentRelationModel) => {
    setDeletionRelation(relation);
  };

  const onRelationSetup = (relation: AgentRelationModel) => {
    setEditingRelation(relation);
  };

  const renderRelationModal = () => {
    return (
      <RelationModal
        relation={creationRelation || editingRelation}
        visible={!!(creationRelation || editingRelation)}
        onClose={onRelationModalClose}
        onSave={onRelationModalSave}
      />
    );
  };

  const renderDeletionConfirmModal = () => {
    const onConfirmClick = async () => {
      if (!deletionRelation) {
        return;
      }

      setItemLoading(true);

      try {
        await defaultApi.deleteAgentRelation(deletionRelation.relation_id);
      } catch (err) {
        addAlert({ content: t('Agent: Relation: Failed to delete relation'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setItemLoading(false);
        return;
      }

      await fetchRelationList();

      setItemLoading(false);
      setDeletionRelation(undefined);
    };

    const onCancelClick = () => {
      setDeletionRelation(undefined);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onConfirmClick}>
              {t('Agent: Relation: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onCancelClick}>
              {t('Agent: Relation: Cancel')}
            </IbButton>
          </>
        }
        loading={itemLoading}
        title={t('Agent: Relation: Delete relation?')}
        visible={!!deletionRelation}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Relation: Are you sure you want to remove the relation and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {relationListIsEmpty ? (
        <div className={EMPTY_CONTENT_CLASS_NAME}>
          <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
            <SourcesEmptyLogo />
            <IbTypography.Paragraph disabled type="secondary">
              {t('Agent: Relation: Empty relation list text')}
            </IbTypography.Paragraph>
            <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreationButtonClick}>
              {t('Agent: Relation: Create')}
            </IbButton>
          </div>
        </div>
      ) : (
        <>
          {listLoading && (
            <div className={SKELETON_CONTAINER_CLASS_NAME}>
              <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
            </div>
          )}
          {!listLoading && (
            <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreationButtonClick}>
              {t('Agent: Relation: Create')}
            </IbButton>
          )}
          <RelationTable
            dataLoading={listLoading}
            pageLoading={pageLoading}
            pagination={relationPagination}
            relations={relationList}
            onDelete={onRelationDelete}
            onPaginationChange={onRelationPaginationChange}
            onRelationStateChange={onRelationStateChange}
            onSetup={onRelationSetup}
          />
        </>
      )}
      {renderRelationModal()}
      {renderDeletionConfirmModal()}
    </div>
  );
};

export default RelationsTab;
